<script>
import { mapGetters } from 'vuex'
import { orderBy } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import BookingLocationListItemSkeleton from '../skeleton/booking-location-list-item-skeleton.vue'
import BookingLocationListItem from './booking-location-list-item.vue'

export default {
  name: 'BookingLocationList',
  components: { BookingLocationListItem, BookingLocationListItemSkeleton },
  mixins: [baseMixin],
  data: () => {
    return {
      selected: null,
      search: '',
    }
  },
  computed: {
    ...mapGetters({
      locations: 'locations/bookingLocations',
      recentLocations: 'bookings/recentLocations',
      isLoadingBookingLocations: 'locations/isLoadingBookingLocations',
      countAllAvailableBookingLocations:
        'auth/countAllAvailableBookingLocations',
    }),
    filteredLocations() {
      return this.sortedLocations.filter((location) =>
        this.filterFunction(location)
      )
    },
    filteredRecentLocations() {
      return this.recentLocationList.filter((location) =>
        this.filterFunction(location)
      )
    },
    alphabeticallyOrderedLocations() {
      return orderBy(this.locations, ['locationName', ['asc']])
    },
    recentLocationList() {
      const mostRecentLocationsList = []

      if (this.recentLocations.length > 0) {
        const recentSorted = orderBy(
          this.recentLocations,
          ['timestamp'],
          ['desc']
        )
        for (const recent of recentSorted) {
          mostRecentLocationsList.push(
            this.locations.find((x) => x.locationId === recent.locationId)
          )
        }
      }

      return mostRecentLocationsList
    },
    sortedLocations() {
      const mostRecentLocationsList = this.recentLocationList
      const orderedLocations = this.alphabeticallyOrderedLocations

      const recentMappedList = mostRecentLocationsList.map((y) => y.locationId)

      return orderedLocations.filter(
        (x) => !recentMappedList.includes(x.locationId)
      )
    },
  },
  methods: {
    selectLocation(locationId) {
      this.$emit(
        'selectLocation',
        this.filteredLocations.find((x) => x.locationId === locationId)
      )
    },
    selectRecentLocation(locationId) {
      this.$emit(
        'selectLocation',
        this.filteredRecentLocations.find((x) => x.locationId === locationId)
      )
    },
    filterFunction(row) {
      // FILTER Search Query
      if (this.search) {
        const str = this.search.toLowerCase().replace(/ /g, '')
        const searchableColumns = `${row.locationName}${row.clientName}${row.addressLine1}${row.addressLine2}${row.suburb}${row.state}${row.postcode}${row.country}`
          .toLowerCase()
          .replace(/ /g, '')

        if (
          !searchableColumns.includes(str) &&
          !str.includes(searchableColumns)
        ) {
          return false
        }
      }

      return true
    },
  },
}
</script>

<template>
  <v-row class="justify-center">
    <v-col :cols="12" md="8">
      <v-card class="transparent" elevation="0">
        <div
          :class="[
            isMobileViewPort ? '' : 'd-flex justify-space-between align-center',
          ]"
        >
          <header>
            <v-card-title>
              {{ $t('booking.locationSelectionTitle') }}
            </v-card-title>
            <v-card-subtitle>
              {{ $t('booking.locationSelectionSubtitle') }}
            </v-card-subtitle>
          </header>
        </div>

        <section>
          <v-text-field
            v-model="search"
            label="Search"
            prepend-icon="mdi-magnify"
            clearable
          ></v-text-field>
        </section>

        <v-card-text
          v-if="
            !isLoadingBookingLocations && filteredRecentLocations.length > 0
          "
        >
          <p>
            <v-icon left small>mdi-history</v-icon>
            {{ $t('booking.locationSelectionMostRecentTitle') }}
          </p>
          <v-row>
            <v-col
              v-for="location in filteredRecentLocations"
              :key="`booking-location-col-recent-${location.locationId}`"
              cols="12"
              md="12"
              lg="6"
              xl="4"
            >
              <BookingLocationListItem
                class="booking-location-list-item"
                :data-location-id="location.locationId"
                :location="location"
                @itemClicked="selectRecentLocation"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text>
          <p v-if="!isLoadingBookingLocations && filteredLocations.length > 0">
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            {{ $t('booking.locationSelectionLocationTitle') }}
          </p>
          <v-row v-if="isLoadingBookingLocations">
            <v-col
              v-for="(location, index) in countAllAvailableBookingLocations"
              :key="`booking-location-skeleton-col-${index}`"
              cols="12"
              md="12"
              lg="6"
              xl="4"
            >
              <BookingLocationListItemSkeleton />
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col
              v-for="location in filteredLocations"
              :key="`booking-location-col-${location.locationId}`"
              cols="12"
              md="12"
              lg="6"
              xl="4"
            >
              <BookingLocationListItem
                class="booking-location-list-item"
                :data-location-id="location.locationId"
                :location="location"
                @itemClicked="selectLocation"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>
